/** Map câu hỏi Hub chat → section B1 trên EvaluatePage; append UI local (không gọi API). */
export type B1ApplySection =
  | 'industry_csf'
  | 'company_strengths'
  | 'competitor_strengths'
  | 'competitive_advantage'
  | 'competitors';

export interface B1ApplyTarget {
  section: B1ApplySection;
  label: string;
  /** Agents whose candidates are preferred for this section */
  agents: string[];
}

export interface B1ApplyCandidate {
  text: string;
  agent?: string;
  /** B6: tên KPI + chỉ tiêu đề xuất (điền vào bảng Đo lường chỉ số). */
  kpi?: string;
  chi_tieu?: string;
}

const fold = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase();

export const B1_APPLY_TARGETS: Record<B1ApplySection, B1ApplyTarget> = {
  industry_csf: {
    section: 'industry_csf',
    label: 'Yếu tố thành công ngành',
    agents: ['success_factor'],
  },
  company_strengths: {
    section: 'company_strengths',
    label: 'Điểm mạnh công ty',
    agents: ['competitive'],
  },
  competitor_strengths: {
    section: 'competitor_strengths',
    label: 'Điểm mạnh đối thủ',
    agents: ['competitive'],
  },
  competitive_advantage: {
    section: 'competitive_advantage',
    label: 'Lợi thế cạnh tranh',
    agents: ['competitive'],
  },
  competitors: {
    section: 'competitors',
    label: 'Đối thủ cạnh tranh',
    agents: ['competitive'],
  },
};

export function detectB1ApplyTarget(userMessage: string, agents: string[] = []): B1ApplyTarget | null {
  const q = fold(userMessage);
  if (/yeu to thanh cong|\bcsf\b|critical success/.test(q)) return B1_APPLY_TARGETS.industry_csf;
  if (/diem manh doi thu|doi thu.*(manh|strength)/.test(q)) return B1_APPLY_TARGETS.competitor_strengths;
  if (/diem manh (cong ty|doanh nghiep)|manh cua (toi|cong ty|doanh nghiep)/.test(q)) {
    return B1_APPLY_TARGETS.company_strengths;
  }
  if (/loi the canh tranh|loi the ben vung|\bvrio\b/.test(q)) return B1_APPLY_TARGETS.competitive_advantage;
  if (/(danh sach )?doi thu|competitors?/.test(q) && !/diem manh|loi the|yeu to/.test(q)) {
    return B1_APPLY_TARGETS.competitors;
  }
  if (agents.includes('success_factor')) return B1_APPLY_TARGETS.industry_csf;
  if (agents.includes('competitive')) return B1_APPLY_TARGETS.competitive_advantage;
  return null;
}

/** Lấy các ý dạng list từ câu trả lời markdown khi không có candidates từ chuyên gia. */
export function extractAnswerBullets(content: string, max = 20): string[] {
  const lines = sanitizeLines(content)
    .split('\n')
    .map((line) =>
      line
        .replace(/^#{1,6}\s+/, '')
        .replace(/^[\s>*]*([-*•]|\d+[.)])\s+/, '')
        .replace(/\*\*/g, '')
        .replace(/^\[[^\]]+\]\s*/, '')
        .trim(),
    )
    .filter((line) => line.length >= 8 && line.length <= 400)
    .filter((line) => !/^(nguon|source|chuyen gia)\b/i.test(line));
  const unique: string[] = [];
  const seen = new Set<string>();
  for (const line of lines) {
    const key = fold(line);
    if (seen.has(key)) continue;
    seen.add(key);
    unique.push(line);
    if (unique.length >= max) break;
  }
  return unique;
}

function sanitizeLines(content: string) {
  return content
    .replace(/<\/?untrusted[a-z0-9_-]*\b[^>]*>/gi, '')
    .replace(/<\/?untrusted[a-z0-9_-]*/gi, '');
}

export function textsFromCandidates(
  candidates: B1ApplyCandidate[],
  fallbackContent: string,
  preferredAgents: string[] = [],
): string[] {
  const scoped = preferredAgents.length
    ? candidates.filter((c) => !c.agent || preferredAgents.includes(c.agent))
    : candidates;
  const pool = scoped.length ? scoped : candidates;
  const fromAgents = pool.map((c) => (c.text || '').trim()).filter((t) => t.length >= 4);
  if (fromAgents.length) {
    const seen = new Set<string>();
    const out: string[] = [];
    for (const t of fromAgents) {
      const key = fold(t);
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(t);
      if (out.length >= 30) break;
    }
    return out;
  }
  return extractAnswerBullets(fallbackContent);
}

/** Append vào state UI EvaluatePage — không gọi API lưu. */
export function appendB1TextsToPage(section: B1ApplySection, texts: string[]) {
  const clean = texts.map((t) => t.trim()).filter(Boolean);
  if (!clean.length) throw new Error('Không có nội dung để thêm.');
  window.dispatchEvent(
    new CustomEvent('bsc-ai-append-b1', {
      detail: { section, texts: clean },
    }),
  );
}
